/**
 * Service Areas Hooks
 *
 * React Query hooks for managing scheduler service area configuration.
 * Provides queries and mutations for CRUD operations on service areas.
 *
 * @see /supabase/migrations/20251206000001_create_scheduler_service_areas.sql
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  ServiceArea,
  ServiceAreaFormData,
  ServiceAreasQueryOptions,
  ServiceAreasResponse,
} from '../types'

/**
 * Query key factory for service areas
 */
const serviceAreasKeys = {
  all: ['scheduler-service-areas'] as const,
  lists: () => [...serviceAreasKeys.all, 'list'] as const,
  list: (filters: ServiceAreasQueryOptions) => [...serviceAreasKeys.lists(), filters] as const,
  detail: (id: string) => [...serviceAreasKeys.all, 'detail', id] as const,
}

/**
 * Fetch service areas from database
 */
async function fetchServiceAreas(
  options: ServiceAreasQueryOptions = {}
): Promise<ServiceAreasResponse> {
  let query = supabase.from('scheduler_service_areas').select('*', { count: 'exact' })

  // Apply filters
  if (options.franchise_region) {
    query = query.eq('franchise_region', options.franchise_region)
  }

  if (options.is_active !== undefined) {
    query = query.eq('is_active', options.is_active)
  }

  // Apply ordering
  const orderBy = options.orderBy || 'display_order'
  query = query.order(orderBy, { ascending: true })

  const { data, error, count } = await query

  if (error) {
    throw new Error(`Failed to fetch service areas: ${error.message}`)
  }

  return {
    areas: (data as ServiceArea[]) || [],
    totalCount: count || 0,
  }
}

/**
 * Fetch single service area by ID
 */
async function fetchServiceArea(id: string): Promise<ServiceArea> {
  const { data, error } = await supabase
    .from('scheduler_service_areas')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    throw new Error(`Failed to fetch service area: ${error.message}`)
  }

  if (!data) {
    throw new Error('Service area not found')
  }

  return data as ServiceArea
}

/**
 * Create new service area
 */
async function createServiceArea(formData: ServiceAreaFormData): Promise<ServiceArea> {
  const { data, error } = await supabase
    .from('scheduler_service_areas')
    .insert([formData])
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create service area: ${error.message}`)
  }

  return data as ServiceArea
}

/**
 * Update existing service area
 */
async function updateServiceArea(
  id: string,
  formData: Partial<ServiceAreaFormData>
): Promise<ServiceArea> {
  const { data, error } = await supabase
    .from('scheduler_service_areas')
    .update(formData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update service area: ${error.message}`)
  }

  return data as ServiceArea
}

/**
 * Delete service area
 */
async function deleteServiceArea(id: string): Promise<void> {
  const { error } = await supabase.from('scheduler_service_areas').delete().eq('id', id)

  if (error) {
    throw new Error(`Failed to delete service area: ${error.message}`)
  }
}

/**
 * Hook: Query service areas
 *
 * @example
 * const { data, isLoading } = useServiceAreas({ franchise_region: 'hamilton' })
 */
export function useServiceAreas(options: ServiceAreasQueryOptions = {}) {
  return useQuery({
    queryKey: serviceAreasKeys.list(options),
    queryFn: () => fetchServiceAreas(options),
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook: Query single service area
 *
 * @example
 * const { data, isLoading } = useServiceArea('uuid-here')
 */
export function useServiceArea(id: string | null) {
  return useQuery({
    queryKey: serviceAreasKeys.detail(id || ''),
    queryFn: () => fetchServiceArea(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook: Create service area mutation
 *
 * @example
 * const { mutate, isPending } = useCreateServiceArea()
 * mutate({ name: 'Toronto', franchise_region: 'hamilton', ... })
 */
export function useCreateServiceArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createServiceArea,
    onSuccess: () => {
      // Invalidate all service area queries
      queryClient.invalidateQueries({ queryKey: serviceAreasKeys.all })
    },
  })
}

/**
 * Hook: Update service area mutation
 *
 * @example
 * const { mutate } = useUpdateServiceArea()
 * mutate({ id: 'uuid-here', data: { is_active: false } })
 */
export function useUpdateServiceArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ServiceAreaFormData> }) =>
      updateServiceArea(id, data),
    onSuccess: (updatedArea) => {
      // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: serviceAreasKeys.lists() })
      // Update specific detail query
      queryClient.setQueryData(serviceAreasKeys.detail(updatedArea.id), updatedArea)
    },
  })
}

/**
 * Hook: Delete service area mutation
 *
 * @example
 * const { mutate } = useDeleteServiceArea()
 * mutate('uuid-here')
 */
export function useDeleteServiceArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteServiceArea,
    onSuccess: () => {
      // Invalidate all service area queries
      queryClient.invalidateQueries({ queryKey: serviceAreasKeys.all })
    },
  })
}

/**
 * Hook: Fetch service areas for a specific franchise
 * Convenience wrapper around useServiceAreas
 *
 * @example
 * const { data } = useServiceAreasByFranchise('hamilton')
 */
export function useServiceAreasByFranchise(franchise: 'hamilton' | 'northyork') {
  return useServiceAreas({ franchise_region: franchise, is_active: true })
}

/**
 * Hook: Fetch all active service areas
 * Convenience wrapper around useServiceAreas
 *
 * @example
 * const { data } = useActiveServiceAreas()
 */
export function useActiveServiceAreas() {
  return useServiceAreas({ is_active: true })
}
